import { useState } from 'react';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import { Form, SelectInput, TextInput, useCreate, useNotify, useTranslate } from 'react-admin';
import { PersonAddAltOutlined } from '@mui/icons-material';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import Box from '@mui/material/Box';
import { useNavigate, useParams } from 'react-router';
import Joi from 'joi';
import { phoneExtension } from '@/utils/phoneValidation';
import { merge, set } from 'lodash';
import { joiResolver } from '@hookform/resolvers/joi';
import useGetManagerProfile from '@/hooks/useGetManagerProfile';
import {
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
    BIG_SUB_MANAGER_CLIENT_TAG,
    GROUP_USERS,
    SUB_MANAGER_TAG,
    USERS,
} from '@/utils/CONST';
import useGetCurrentUser from '@/hooks/useGetCurrentUser';
import { useUpdate } from 'react-admin';
import { User } from '@/utils/types';
import { useQueryClient } from '@tanstack/react-query';
import { composeUri } from '@/utils/compose';

interface Props {
    select?: boolean;
}

export const USER_PROFILES = [BIG_AGENCY_MANAGER, BIG_AGENCY_OPERATOR];

const CreateAgencyUser = ({ select = false }: Props) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();
    const { currentUser, isLoading: isLoadingUser } = useGetCurrentUser();
    const notify = useNotify();
    const { agencyId } = useParams();
    // const [selectedAgenciesId] = useRecordSelection(GROUP_USERS);
    const queryClient = useQueryClient();

    const navigateBack = () => {
        navigate(-1);
    };

    const [selectedProfile, setSelectedProfile] = useState<string>();
    const { profile, isLoading: isLoadingProfile } = useGetManagerProfile({
        profileName: selectedProfile,
    });

    const customJoi = Joi.extend(phoneExtension);
    const schema = customJoi
        .object(
            merge(
                {},
                set({}, 'firstName', Joi.string().required()),
                set({}, 'lastName', Joi.string().required()),
                select ? set({}, 'tags', Joi.string().required()) : {},
                set(
                    {},
                    'email',
                    Joi.string()
                        .email({ tlds: { allow: false } })
                        .required(),
                ),
            ),
        )
        .messages({
            'any.required': translate('ra.validation.required'),
            'string.base': translate('ra.validation.required'),
            'string.email': translate('ra.validation.email'),
        });

    const handleSubmit = (values: any) => {
        let user_tags = [SUB_MANAGER_TAG, BIG_SUB_MANAGER_CLIENT_TAG, values.tags];
        delete values.tags;

        create(
            USERS,
            {
                data: {
                    ...values,
                    username: values['email'],
                    company: currentUser?.company,
                    tags: user_tags,
                    profiles: [profile?.id],
                },
                meta: {
                    realResource: `v2/${USERS}`,
                },
            },
            {
                onSuccess: (user: User) => {
                    update(
                        USERS,
                        {
                            id: user.id + '/add_group_users',
                            data: {
                                groupUsers: [composeUri(GROUP_USERS, agencyId)],
                            },
                        },
                        {
                            onSuccess: () => {
                                queryClient.invalidateQueries({ queryKey: [`${GROUP_USERS}/${agencyId}/${USERS}`] });
                                queryClient.invalidateQueries({ queryKey: [USERS] });
                                notify('ra.notification.created', { type: 'success' });
                                navigateBack();
                            },
                        },
                    );
                },
                onError: ({ response }: any) => {
                    notify(`messages.${response.data['@type']}`);
                },
            },
        );
    };

    return (
        <DialogTemplate
            closeButtonDisabled={isLoadingCreate || isLoadingUpdate}
            title={translate('main.navigation.add_user')}
            icon={<PersonAddAltOutlined />}
            open={true}
        >
            <Form resolver={joiResolver(schema)} onSubmit={handleSubmit} reValidateMode={'onBlur'}>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        {[
                            { source: 'lastName', label: 'main.navigation.last_name' },
                            { source: 'firstName', label: 'main.navigation.name' },
                            { source: 'email', label: 'main.navigation.email' },
                        ].map(({ source, label }, index) => (
                            <TextInput key={index} helperText={false} name={source} source={source} label={label} />
                        ))}
                        {select && (
                            <SelectInput
                                helperText={false}
                                label={'users.list.user_type'}
                                source={'tags'}
                                onChange={(e: any) => {
                                    setSelectedProfile(e.target.value);
                                }}
                                choices={USER_PROFILES.map((v: string) => ({
                                    id: v,
                                    name: `users.profiles.${v.toLowerCase()}`,
                                }))}
                            />
                        )}
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button onClick={navigateBack} disabled={isLoadingCreate || isLoadingUpdate}>
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton
                            data-cy={'create-user-button'}
                            loading={isLoadingCreate || isLoadingProfile || isLoadingUser || isLoadingUpdate}
                            disabled={!agencyId}
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.create')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default CreateAgencyUser;
